import { tr } from '@/lib/i18n-core';
import { useEffect, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, PauseCircle, Send, Trash2 } from 'lucide-react';
import {
  SMTP_ENCRYPTIONS,
  type EmailKind,
  type EmailLogPageDto,
  type EmailLogStatus,
  type EmailQuotaDto,
  type SmtpEncryption,
  type TenantEmailSettingsDto,
} from '@his/shared';
import { del, get, post, put, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateTime } from '@/lib/utils';
import { Dialog } from '@/components/ui/dialog';
import { Alert, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Field, Input, PageSkeleton, PasswordInput, Select } from '@/components/ui/primitives';
import { DataTable } from '@/components/ui/layout';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';

export const EMAIL_SETTINGS_KEY = ['email-settings'] as const;
const LOG_PAGE = 50;

const ENCRYPTION_LABEL: Record<SmtpEncryption, string> = {
  STARTTLS: 'email_encryption_starttls',
  SSL: 'email_encryption_ssl',
  NONE: 'email_encryption_none',
};

const DEFAULT_PORT: Record<SmtpEncryption, string> = { STARTTLS: '587', SSL: '465', NONE: '25' };

const STATUS_BADGE: Record<EmailLogStatus, 'success' | 'destructive' | 'muted'> = { sent: 'success', failed: 'destructive', skipped: 'muted' };

export function emailKindLabel(kind: EmailKind): string {
  return tr('admin', `email_kind_${kind}`);
}

export function quotaLine(used: number, limit: number): string {
  return limit > 0 ? tr('admin', 'email_of_limit', { used, limit }) : tr('admin', 'email_no_limit', { used });
}

interface FormState {
  host: string;
  port: string;
  username: string;
  password: string;
  encryption: SmtpEncryption;
  fromEmail: string;
  fromName: string;
}

function formOf(settings: TenantEmailSettingsDto): FormState {
  const smtp = settings.smtp;
  return {
    host: smtp.host || 'smtppro.zoho.com',
    port: smtp.port ? String(smtp.port) : '587',
    username: smtp.username,
    password: '',
    encryption: smtp.encryption,
    fromEmail: smtp.fromEmail,
    fromName: smtp.fromName,
  };
}

function useStepUpNeeded(): boolean {
  const { user } = useAuth();
  return Boolean(user?.has2fa) && !user?.impersonation;
}

function otpHeaders(code: string) {
  return code ? { headers: { 'X-Approver-Otp': code } } : {};
}

function UsageCard({ quota }: { quota: EmailQuotaDto }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="rounded-md border p-3">
        <p className="text-xs text-muted-foreground">{tr('admin', 'email_used_today')}</p>
        <p className="font-mono text-sm">{quotaLine(quota.usedToday, quota.dailyLimit)}</p>
      </div>
      <div className="rounded-md border p-3">
        <p className="text-xs text-muted-foreground">{tr('admin', 'email_used_month')}</p>
        <p className="font-mono text-sm">{quotaLine(quota.usedThisMonth, quota.monthlyAllowance)}</p>
      </div>
    </div>
  );
}

function SendingCard({ settings }: { settings: TenantEmailSettingsDto }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isSuperAdmin, canWrite } = useAuth();
  const resume = useMutation({
    mutationFn: () => post<TenantEmailSettingsDto>('/email/resume'),
    onSuccess: (next) => {
      queryClient.setQueryData(EMAIL_SETTINGS_KEY, next);
      void queryClient.invalidateQueries({ queryKey: ['email-status'] });
      toast.success(tr('admin', 'email_resumed'));
    },
    onError: (error) => toast.error(tr('admin', 'email_resume_failed'), describeError(error)),
  });
  const pause = settings.pause;
  const throughLine =
    settings.sendsThrough === 'tenant_smtp'
      ? tr('admin', 'email_sends_through_tenant')
      : settings.sendsThrough === 'owner_smtp'
        ? tr('admin', 'email_sends_through_owner')
        : tr('admin', 'email_sends_nowhere');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <Send className="h-4 w-4" /> {tr('admin', 'email_sending_title')}
        </CardTitle>
        <CardDescription>{throughLine}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {pause.paused && (
          <Alert variant="warning">
            <p className="flex items-center gap-2 font-medium">
              <PauseCircle className="h-4 w-4" /> {tr('admin', 'email_paused_title')}
            </p>
            <p className="mt-1">
              {tr('admin', pause.reason === 'monthly_limit' ? 'email_paused_monthly' : 'email_paused_daily', { date: dateTime(pause.pausedAt) })}
            </p>
            {!settings.canResume && <p className="mt-1">{tr('admin', 'email_resume_wait')}</p>}
            {!isSuperAdmin && <p className="mt-1">{tr('admin', 'email_resume_super_admin')}</p>}
          </Alert>
        )}
        {pause.paused && isSuperAdmin && canWrite && (
          <Button size="sm" disabled={!settings.canResume} loading={resume.isPending} onClick={() => resume.mutate()}>
            {tr('admin', 'email_resume')}
          </Button>
        )}
        {!settings.ownerFallbackAllowed && <p className="text-xs text-muted-foreground">{tr('admin', 'email_fallback_off')}</p>}
        {settings.sendsThrough !== 'tenant_smtp' && <UsageCard quota={settings.quota} />}
      </CardContent>
    </Card>
  );
}

function SmtpCard({ settings, readOnly }: { settings: TenantEmailSettingsDto; readOnly: boolean }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const needsCode = useStepUpNeeded();
  const [form, setForm] = useState<FormState>(() => formOf(settings));
  const [clearPassword, setClearPassword] = useState(false);
  const [code, setCode] = useState('');
  const [testTo, setTestTo] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(false);
  const smtp = settings.smtp;

  useEffect(() => {
    setForm(formOf(settings));
    setClearPassword(false);
  }, [settings]);

  const settle = (next: TenantEmailSettingsDto) => {
    queryClient.setQueryData(EMAIL_SETTINGS_KEY, next);
    setCode('');
  };

  const save = useMutation({
    mutationFn: () =>
      put<TenantEmailSettingsDto>(
        '/email/smtp',
        {
          host: form.host.trim(),
          port: Number.parseInt(form.port, 10),
          username: form.username.trim(),
          ...(clearPassword ? { password: '' } : form.password ? { password: form.password } : {}),
          encryption: form.encryption,
          fromEmail: form.fromEmail.trim(),
          fromName: form.fromName.trim(),
        },
        otpHeaders(code),
      ),
    onSuccess: (next) => {
      settle(next);
      toast.success(tr('admin', 'email_saved'));
    },
    onError: (error) => toast.error(tr('admin', 'email_save_failed'), describeError(error)),
  });

  const remove = useMutation({
    mutationFn: () => del<TenantEmailSettingsDto>('/email/smtp', otpHeaders(code)),
    onSuccess: (next) => {
      settle(next);
      setConfirmRemove(false);
      toast.success(tr('admin', 'email_removed'));
    },
    onError: (error) => toast.error(tr('admin', 'email_save_failed'), describeError(error)),
  });

  const test = useMutation({
    mutationFn: () => post<TenantEmailSettingsDto>('/email/smtp/test', { toEmail: testTo.trim() }),
    onSuccess: settle,
    onError: (error) => toast.error(tr('admin', 'email_test_send'), describeError(error)),
  });

  const port = Number.parseInt(form.port, 10);
  const codeReady = !needsCode || code.length === 6;
  const valid = form.host.trim().length > 0 && Number.isInteger(port) && port >= 1 && port <= 65535 && /\S+@\S+\.\S+/.test(form.fromEmail);
  const update = (patch: Partial<FormState>) => setForm({ ...form, ...patch });
  const passwordHint = smtp.hasPassword
    ? clearPassword
      ? tr('admin', 'email_password_cleared')
      : tr('admin', 'email_password_stored')
    : tr('admin', 'email_password_none');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <Mail className="h-4 w-4" /> {tr('admin', 'email_smtp_title')}
        </CardTitle>
        <CardDescription>{tr('admin', 'email_smtp_hint')}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <Field label={tr('admin', 'email_host')} htmlFor="smtp-host" className="sm:col-span-2" required>
          <Input id="smtp-host" dir="ltr" value={form.host} disabled={readOnly} onChange={(e) => update({ host: e.target.value })} />
        </Field>
        <Field label={tr('admin', 'email_encryption')} htmlFor="smtp-encryption">
          <ChoiceButtons
            id="smtp-encryption"
            value={form.encryption}
            disabled={readOnly}
            onChange={(encryption) => update({ encryption, port: DEFAULT_PORT[encryption] })}
            options={SMTP_ENCRYPTIONS.map((value) => ({ value, label: tr('admin', ENCRYPTION_LABEL[value]) }))}
          />
        </Field>
        <Field label={tr('admin', 'email_port')} htmlFor="smtp-port" required>
          <Input id="smtp-port" dir="ltr" inputMode="numeric" value={form.port} disabled={readOnly} onChange={(e) => update({ port: e.target.value.replace(/\D/g, '').slice(0, 5) })} />
        </Field>
        <Field label={tr('admin', 'email_username')} htmlFor="smtp-user">
          <Input id="smtp-user" dir="ltr" autoComplete="off" value={form.username} disabled={readOnly} onChange={(e) => update({ username: e.target.value })} />
        </Field>
        <Field label={tr('admin', 'email_app_password')} htmlFor="smtp-password" hint={passwordHint}>
          <div className="flex gap-2">
            <PasswordInput
              id="smtp-password"
              autoComplete="new-password"
              value={form.password}
              disabled={readOnly || clearPassword}
              onChange={(e) => update({ password: e.target.value })}
            />
            {smtp.hasPassword && !readOnly && (
              <Button type="button" variant="ghost" size="sm" onClick={() => setClearPassword(!clearPassword)}>
                {clearPassword ? tr('admin', 'email_password_keep') : tr('admin', 'email_password_clear')}
              </Button>
            )}
          </div>
        </Field>
        <Field label={tr('admin', 'email_from_email')} htmlFor="smtp-from" required>
          <Input id="smtp-from" dir="ltr" type="email" value={form.fromEmail} disabled={readOnly} onChange={(e) => update({ fromEmail: e.target.value })} />
        </Field>
        <Field label={tr('admin', 'email_from_name')} htmlFor="smtp-from-name" hint={tr('admin', 'email_from_name_hint')}>
          <Input id="smtp-from-name" value={form.fromName} disabled={readOnly} onChange={(e) => update({ fromName: e.target.value })} />
        </Field>

        {!readOnly && needsCode && (
          <Field label={tr('admin', 'authenticator_code')} hint={tr('admin', 'step_up_hint')} htmlFor="smtp-otp" required>
            <Input
              id="smtp-otp"
              dir="ltr"
              inputMode="numeric"
              maxLength={6}
              className="tracking-[0.4em]"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            />
          </Field>
        )}

        {!readOnly && (
          <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
            <Button loading={save.isPending} disabled={!valid || !codeReady} onClick={() => save.mutate()}>
              {tr('admin', 'email_save')}
            </Button>
            {smtp.configured && (
              <Button variant="ghost" className="gap-1.5 text-destructive" onClick={() => setConfirmRemove(true)}>
                <Trash2 className="h-3.5 w-3.5" /> {tr('admin', 'email_remove')}
              </Button>
            )}
          </div>
        )}

        {smtp.configured && (
          <div className="space-y-2 border-t pt-3 sm:col-span-2">
            <div className="flex flex-wrap items-end gap-2">
              <Field label={tr('admin', 'email_test_to')} htmlFor="smtp-test-to" className="min-w-[220px] flex-1">
                <Input id="smtp-test-to" dir="ltr" type="email" value={testTo} disabled={readOnly} onChange={(e) => setTestTo(e.target.value)} />
              </Field>
              {!readOnly && (
                <Button variant="outline" loading={test.isPending} disabled={!/\S+@\S+\.\S+/.test(testTo)} onClick={() => test.mutate()}>
                  {tr('admin', 'email_test_send')}
                </Button>
              )}
            </div>
            <p className={smtp.lastTestOk === false ? 'text-xs text-destructive' : 'text-xs text-muted-foreground'}>
              {smtp.lastTestAt
                ? smtp.lastTestOk
                  ? tr('admin', 'email_test_ok', { date: dateTime(smtp.lastTestAt) })
                  : tr('admin', 'email_test_failed', { date: dateTime(smtp.lastTestAt), error: smtp.lastTestError ?? '' })
                : tr('admin', 'email_test_never')}
            </p>
          </div>
        )}
      </CardContent>

      {confirmRemove && (
        <Dialog
          open
          onClose={() => setConfirmRemove(false)}
          title={tr('admin', 'email_remove')}
          description={tr('admin', 'email_remove_confirm')}
          footer={
            <>
              <Button variant="ghost" onClick={() => setConfirmRemove(false)} disabled={remove.isPending}>
                {tr('admin', 'cancel')}
              </Button>
              <Button variant="destructive" loading={remove.isPending} disabled={!codeReady} onClick={() => remove.mutate()}>
                {tr('admin', 'email_remove')}
              </Button>
            </>
          }
        >
          {needsCode ? (
            <Field label={tr('admin', 'authenticator_code')} hint={tr('admin', 'step_up_hint')} htmlFor="smtp-remove-otp" required>
              <Input
                id="smtp-remove-otp"
                dir="ltr"
                inputMode="numeric"
                maxLength={6}
                className="tracking-[0.4em]"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              />
            </Field>
          ) : null}
        </Dialog>
      )}
    </Card>
  );
}

export function EmailLogTable({ path, queryKey }: { path: string; queryKey: readonly unknown[] }) {
  const [status, setStatus] = useState<EmailLogStatus | ''>('');
  const logs = useInfiniteQuery({
    queryKey: [...queryKey, status],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => get<EmailLogPageDto>(`${path}${qs({ status: status || undefined, limit: LOG_PAGE, cursor: pageParam })}`),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = logs.data?.pages.flatMap((page) => page.data);

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Select aria-label={tr('admin', 'email_log_status')} className="w-44" value={status} onChange={(e) => setStatus(e.target.value as EmailLogStatus | '')}>
          <option value="">{tr('admin', 'email_log_all')}</option>
          <option value="sent">{tr('admin', 'email_status_sent')}</option>
          <option value="failed">{tr('admin', 'email_status_failed')}</option>
          <option value="skipped">{tr('admin', 'email_status_skipped')}</option>
        </Select>
      </div>
      <DataTable
        rows={rows}
        loading={logs.isLoading}
        refreshing={logs.isFetching}
        error={logs.error}
        rowKey={(row) => row.id}
        empty={tr('admin', 'email_log_empty')}
        columns={[
          { header: tr('admin', 'email_log_when'), cell: (row) => dateTime(row.createdAt) },
          { header: tr('admin', 'email_log_to'), cell: (row) => <span dir="ltr" className="font-mono text-xs">{row.recipients.join(', ')}</span> },
          { header: tr('admin', 'email_log_subject'), cell: (row) => <span className="line-clamp-2 max-w-[280px]">{row.subject}</span> },
          { header: tr('admin', 'email_log_kind'), cell: (row) => emailKindLabel(row.kind) },
          {
            header: tr('admin', 'email_log_status'),
            cell: (row) => (
              <div className="space-y-0.5">
                <Badge variant={STATUS_BADGE[row.status]}>{tr('admin', `email_status_${row.status}`)}</Badge>
                {row.error && <p className="max-w-[260px] text-xs text-muted-foreground">{row.error}</p>}
              </div>
            ),
          },
          {
            header: tr('admin', 'email_log_via'),
            cell: (row) => (row.via === 'tenant_smtp' ? tr('admin', 'email_via_tenant') : row.via === 'owner_smtp' ? tr('admin', 'email_via_owner') : '—'),
          },
        ]}
        footer={
          logs.hasNextPage && (
            <Button variant="outline" size="sm" onClick={() => void logs.fetchNextPage()} loading={logs.isFetchingNextPage}>
              {tr('admin', 'show_more')}
            </Button>
          )
        }
      />
    </div>
  );
}

export function EmailSettingsTab({ readOnly }: { readOnly: boolean }) {
  const settings = useQuery({ queryKey: EMAIL_SETTINGS_KEY, queryFn: () => get<TenantEmailSettingsDto>('/email/settings') });

  if (settings.isLoading) return <PageSkeleton />;
  if (settings.error || !settings.data) return <Alert variant="destructive">{describeError(settings.error)}</Alert>;

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <SendingCard settings={settings.data} />
      <SmtpCard settings={settings.data} readOnly={readOnly} />
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle className="text-sm">{tr('admin', 'email_log_title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <EmailLogTable path="/email/logs" queryKey={['email-logs']} />
        </CardContent>
      </Card>
    </div>
  );
}
